import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import FlutterInstallManual from "../../integrate/_snippets/install-flutter-manual.mdx";
export const EnableSessionReplayDark = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-dark-mode.png";
export const EnableSessionReplayLight = "https://res.cloudinary.com/dmukukwp6/image/upload/posthog.com/contents/images/docs/session-replay/enable-session-replay-in-project-settings-light-mode.png";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const ProductScreenshot = makeShortcode("ProductScreenshot");
const layoutProps = {
  EnableSessionReplayDark,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h2 {...{
      "id": "step-one-add-posthog-to-your-app"
    }}>{`Step one: Add PostHog to your app`}</h2>
    <blockquote>
      <p parentName="blockquote">{`Session replay requires PostHog Flutter SDK version >= `}<a parentName="p" {...{
          "href": "https://github.com/PostHog/posthog-flutter/releases"
        }}>{`4.7.0`}</a>{`, and it's recommended to always use `}<a parentName="p" {...{
          "href": "/docs/sdk-doctor"
        }}>{`the latest version`}</a>{`.`}</p>
    </blockquote>


    <blockquote>
      <p parentName="blockquote">{`⚠️ `}<strong parentName="p">{`Note:`}</strong>{` For session replay, you must setup the SDK manually.`}</p>
    </blockquote>
    <FlutterInstallManual mdxType="FlutterInstallManual" />
    <h4 {...{
      "id": "widget-and-observer-setup"
    }}>{`Widget and Observer setup`}</h4>
    <blockquote>
      <p parentName="blockquote">{`This configuration is only needed for mobile session replay.`}</p>
    </blockquote>
    <p>{`Wrap your app with the `}<inlineCode parentName="p">{`PostHogWidget`}</inlineCode>{` Widget and Install the `}<inlineCode parentName="p">{`PosthogObserver`}</inlineCode>{` Observer.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart"
      }}>{`import 'package:flutter/material.dart';

import 'package:posthog_flutter/posthog_flutter.dart';

class MyApp extends StatefulWidget {
  const MyApp({super.key});

  @override
  State<MyApp> createState() => _MyAppState();
}

class _MyAppState extends State<MyApp> {
  @override
  void initState() {
    super.initState();
  }

  @override
  Widget build(BuildContext context) {
    return PostHogWidget(
      child: MaterialApp(
        navigatorObservers: [PosthogObserver()],
        title: 'My App',
        home: const HomeScreen(),
      ),
    );
  }
}
`}</code></pre>
    <p>{`If you're using `}<inlineCode parentName="p">{`go_router`}</inlineCode>{`, check `}<a parentName="p" {...{
        "href": "/docs/libraries/flutter#capturing-screen-views"
      }}>{`this page`}</a>{` to learn how to set up the `}<inlineCode parentName="p">{`PosthogObserver`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "step-two-enable-session-recordings-in-your-project-settings"
    }}>{`Step two: Enable session recordings in your project settings`}</h2>
    <p>{`Enable session recordings in your PostHog `}<a parentName="p" {...{
        "href": "https://app.posthog.com/project/settings"
      }}>{`Project Settings`}</a>{`.`}</p>
    <p>{`If you're using Flutter Web, also enable the `}<a parentName="p" {...{
        "href": "/docs/session-replay/canvas-recording"
      }}>{`Canvas capture`}</a>{` in `}<a parentName="p" {...{
        "href": "https://us.posthog.com/settings/project-replay"
      }}>{`your project settings`}</a>{`.`}<br parentName="p"></br>{`
`}{`This is needed as Flutter renders your app using a browser canvas element.`}</p>
    <ProductScreenshot imageLight={EnableSessionReplayLight} imageDark={EnableSessionReplayDark} alt="Enable session recordings in your PostHog'" classes="rounded" mdxType="ProductScreenshot" />
    <h2 {...{
      "id": "step-three-configure-replay-settings"
    }}>{`Step three: Configure replay settings`}</h2>
    <p>{`Add `}<inlineCode parentName="p">{`sessionReplay = true`}</inlineCode>{` to your PostHog configuration alongside any of your other configuration options:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-dart"
      }}>{`final config = PostHogConfig('<ph_project_api_key>');
/// Enable Recording of Session replay for Android and iOS.
/// Requires Record user sessions to be enabled in the PostHog Project Settings.
/// Defaults to false.
config.sessionReplay = true;
/// Enable masking of all text and text input fields.
/// Default: true.
config.sessionReplayConfig.maskAllTexts = false;
/// Enable masking of all images.
/// Default: true.
config.sessionReplayConfig.maskAllImages = false;
/// Throttling delay used to reduce the number of snapshots captured and reduce performance impact.
/// This is used for capturing the view as a screenshot.
/// The lower the number, the more snapshots will be captured but higher the performance impact.
/// Defaults to 1s.
/// Ps: it was called [debouncerDelay] until version 4.7.1
config.sessionReplayConfig.throttleDelay = const Duration(milliseconds: 1000);
`}</code></pre>
    <h2 {...{
      "id": "limitations-for-mobile-session-replay"
    }}>{`Limitations for mobile session replay`}</h2>
    <ul>
      <li parentName="ul">{`On Android, requires API >= 26.`}</li>
      <li parentName="ul">{`On iOS, minimum deployment target is `}<a parentName="li" {...{
          "href": "/docs/libraries/ios"
        }}>{`iOS13`}</a>{`.`}</li>
      <li parentName="ul">{`Wireframe mode isn't supported, only screenshot mode.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "docs/session-replay/network-recording"
        }}>{`Network performance recording`}</a>{` isn't supported yet.`}</li>
    </ul>
    <h2 {...{
      "id": "limitations-for-flutter-web-session-replay"
    }}>{`Limitations for flutter web session replay`}</h2>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "/docs/session-replay/canvas-recording"
        }}>{`Canvas capture`}</a>{` enabled is required.`}</li>
    </ul>
    <h2 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h2>
    <ul>
      <li parentName="ul">{`Update your SDK and iOS Pods.`}</li>
      <li parentName="ul">{`Make sure to set the minimum platform version to iOS 13.0 in your Podfile.`}</li>
      <li parentName="ul">{`Make sure you have disabled automatic SDK intialization. `}</li>
      <li parentName="ul">{`Run a clean build if you experience issues.`}</li>
      <li parentName="ul">{`For blank recordings for mobile session replay, be sure to set up the `}<a parentName="li" {...{
          "href": "/docs/session-replay/installation?tab=Flutter#widget-and-observer-setup"
        }}>{`Widget and Observer`}</a>{`.`}</li>
      <li parentName="ul">{`For blank recordings for flutter web session replay, be sure to enable `}<a parentName="li" {...{
          "href": "/docs/session-replay/canvas-recording"
        }}>{`Canvas capture`}</a>{`.`}</li>
      <li parentName="ul">{`If you have enabled session replay using feature flags, the flags are evaluated on the device once the PostHog SDK starts as early as possible. The SDK might be using the cached flags from the previous SDK start. If you have changed the flag or its condition, kill and reopen the app to force a new SDK start at least once.`}
        <ul parentName="li">
          <li parentName="ul">{`This will also happen in production, and you might experience some delay for the new flag/conditions to take effect on your users. We're tracking this bug `}<a parentName="li" {...{
              "href": "https://github.com/PostHog/posthog-ios/issues/263"
            }}>{`here`}</a>{`.`}</li>
          <li parentName="ul">{`Session replay feature flag evaluation does not capture `}<inlineCode parentName="li">{`$feature_flag_called`}</inlineCode>{` events, so the `}<inlineCode parentName="li">{`Usage`}</inlineCode>{` tab on the feature flag page won't show anything. We're tracking this feature request `}<a parentName="li" {...{
              "href": "https://github.com/PostHog/posthog-android/issues/250"
            }}>{`here`}</a>{`.`}</li>
        </ul>
      </li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      